import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronDown, Search, X } from "lucide-react";

export type MultiSelectOption = {
  value: string;
  label: string;
  count?: number;
  group?: string; // gom nhóm (vd phân loại theo tên SP)
};

/** Dropdown chọn nhiều: ô tìm kiếm + tích chọn, đóng khi click ra ngoài. */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "— Tất cả —",
  unit = "mục",
  disabled = false,
  disabledText,
  loading = false,
  className = "w-64",
}: {
  options: MultiSelectOption[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  unit?: string;
  disabled?: boolean;
  disabledText?: string;
  loading?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const selected = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.group ?? "").toLowerCase().includes(q)
    );
  }, [options, search]);

  const toggle = (v: string) =>
    onChange(selected.has(v) ? value.filter((x) => x !== v) : [...value, v]);

  const selectAllFiltered = () =>
    onChange([...new Set([...value, ...filtered.map((o) => o.value)])]);

  let summary: string;
  if (disabled && disabledText) summary = disabledText;
  else if (loading) summary = "Đang tải…";
  else if (value.length === 0) summary = placeholder;
  else if (value.length === 1)
    summary = options.find((o) => o.value === value[0])?.label ?? value[0];
  else summary = `Đã chọn ${value.length} ${unit}`;

  let lastGroup: string | undefined;

  return (
    <div ref={ref} className={clsx("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "input w-full flex items-center justify-between gap-2 text-left",
          disabled && "opacity-60 cursor-not-allowed"
        )}
        title={value.length > 1 ? value.map((v) => options.find((o) => o.value === v)?.label ?? v).join("\n") : undefined}
      >
        <span className={clsx("truncate", value.length === 0 && "text-slate-500")}>
          {summary}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value.length > 0 && !disabled && (
            <X
              size={14}
              className="text-slate-400 hover:text-slate-700"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          )}
          <ChevronDown size={14} className="text-slate-400" />
        </span>
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-[min(28rem,90vw)] rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm…"
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="px-2 py-1.5 border-b border-slate-100 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={selectAllFiltered}
              className="text-brand-600 hover:underline"
            >
              Chọn tất cả{search ? " (đang tìm)" : ""}
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-slate-500 hover:underline"
            >
              Bỏ chọn hết
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-slate-500">Không có kết quả</div>
            )}
            {filtered.map((o) => {
              const header =
                o.group !== undefined && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group;
              return (
                <div key={o.value}>
                  {header !== null && (
                    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {header}
                    </div>
                  )}
                  <label className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(o.value)}
                      onChange={() => toggle(o.value)}
                    />
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.count !== undefined && (
                      <span className="text-xs text-slate-400">{o.count}</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
